import authService from '../api-authorization/AuthorizeService';
import { ApiRoutes } from '../Common';

export const EchoOutcomes = {
    applied: 'applied',
    officeMismatch: 'office-mismatch',
    jobNotInList: 'job-not-in-list',
    handlerError: 'handler-error',
    refetched: 'refetched',
    refetchError: 'refetch-error',
    officeFiltered: 'office-filtered',
};

const MAX_DETAIL_LENGTH = 500;

/** Tells this browser tab's reports apart from the same user's other tabs. */
const tabId = window.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2);

const hubConnectionIds = {};

/** Records the hub connection a board is listening on, so its echo and save reports carry it. */
export const setBoardHubConnection = (board, connectionId) => {
    hubConnectionIds[board] = connectionId;
};

/** Prefixes detail with the board label so the legacy board and Dispatch+ can be told apart. */
export const withBoardDetail = (board, detail) =>
    [`board=${board}`, detail].filter(Boolean).join(';').slice(0, MAX_DETAIL_LENGTH);

const buildHeaders = (token) => {
    const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
    const currentTenantId = window.sessionStorage.getItem('currentTenantId');
    const impersonating = window.sessionStorage.getItem('impersonating');

    if (currentTenantId) headers['X-CurrentTenantId'] = currentTenantId;
    if (impersonating) headers['X-Impersonating'] = impersonating;

    return headers;
};

/**
 * Sends one diagnostic report about the dispatch board's live-update path. Fire-and-forget: it never
 * throws, never retries, and a failed report is dropped. It uses a plain fetch with the stored access
 * token instead of httpClient, because httpClient renews the token on a 401 and the renewal reloads
 * the board; a report must never cause that.
 */
export const reportBoardTelemetry = async ({
    event, broadcastId, jobIds, outcome, hubConnectionId, detail,
}) => {
    try {
        const token = await authService.getAccessToken();

        if (!token) return;

        await fetch(ApiRoutes.dispatch.boardTelemetry(), {
            method: 'POST',
            headers: buildHeaders(token),
            body: JSON.stringify({
                event,
                broadcastId,
                jobIds: jobIds?.map(Number).filter(Number.isFinite),
                outcome,
                hubConnectionId,
                detail: [detail, `tab=${tabId}`].filter(Boolean).join(';'),
            }),
        });
    } catch {
        // Telemetry must never disturb the board.
    }
};

/** Reports a hub connection lifecycle event; `board` tells the legacy board and Dispatch+ apart. */
export const reportHubLifecycle = (board, event, { connectionId, error } = {}) => {
    const detail = [`board=${board}`, error ? `error=${error.message ?? error}` : null].filter(Boolean).join(';');

    reportBoardTelemetry({ event, hubConnectionId: connectionId, detail });
};

/** Joins the dispatch group on a hub, reporting a failure; `board` tells the legacy board and Dispatch+ apart. */
export const joinDispatchGroup = async (hub, board) => {
    try {
        await hub.invoke('JoinGroup', 'Dispatch');
    } catch (e) {
        console.info('Unable to join dispatch hub', e);
        reportHubLifecycle(board, 'join-group-failed', { connectionId: hub.connectionId, error: e });
    }
};

export const parseBroadcastMeta = (metaJson) => {
    try {
        return typeof metaJson === 'string' ? JSON.parse(metaJson) : (metaJson ?? null);
    } catch {
        return null;
    }
};

/**
 * Decides what a hub message did on this board. Office is checked first, mirroring the handlers'
 * early return; `anyJobFound` is omitted by handlers that do not look the job up.
 */
export const classifyEchoOutcome = ({
    selectedOfficeId, messageOfficeId, jobIds, anyJobFound,
}) => {
    if (messageOfficeId !== undefined && messageOfficeId !== selectedOfficeId) {
        return {
            outcome: EchoOutcomes.officeMismatch,
            jobIds,
            detail: `selected=${selectedOfficeId};message=${messageOfficeId}`,
        };
    }

    if (anyJobFound === false) {
        return { outcome: EchoOutcomes.jobNotInList, jobIds };
    }

    return { outcome: EchoOutcomes.applied, jobIds };
};

let currentUserSub = null;

const reportEcho = async (meta, echo, board) => {
    try {
        if (!meta?.userId) return;

        if (!currentUserSub) {
            currentUserSub = (await authService.getUser())?.sub ?? null;
        }

        if (currentUserSub === meta.userId) {
            reportBoardTelemetry({
                event: 'echo',
                broadcastId: meta.broadcastId,
                hubConnectionId: hubConnectionIds[board],
                ...echo,
                detail: withBoardDetail(board, echo.detail),
            });
        }
    } catch {
        // Telemetry must never disturb the board.
    }
};

/**
 * Runs a hub-message handler body and reports what happened, but only for messages this user's own
 * save caused. `run` returns `{ value, echo }`; `value` is handed back to the caller. A throw is
 * reported as `handler-error` and rethrown.
 */
export const withEchoTelemetry = (metaJson, run) => {
    const meta = parseBroadcastMeta(metaJson);
    let result;

    try {
        result = run();
    } catch (e) {
        reportEcho(meta, { outcome: EchoOutcomes.handlerError, detail: e?.message }, 'legacy');
        throw e;
    }

    reportEcho(meta, result.echo, 'legacy');

    return result.value;
};

/** Reports a successful save; a response without `successDates` saved nothing and reports nothing. */
export const reportSaveOk = (board, response, items) => {
    if (response?.successDates?.length) {
        reportBoardTelemetry({
            event: 'save-ok',
            broadcastId: response.broadcastId,
            hubConnectionId: hubConnectionIds[board],
            jobIds: [...new Set(items.map((i) => i.jobId))],
            detail: withBoardDetail(board),
        });
    }
};

const toList = (value) => (Array.isArray(value) ? value : [value]).filter((v) => v !== undefined && v !== null);

/** Flattens every payload shape a job hub message can take into its job rows (or bare ids). */
const payloadRows = (payload) => {
    if (payload && typeof payload === 'object' && !Array.isArray(payload)
        && ('created' in payload || 'updated' in payload || 'deleted' in payload)) {
        return [payload.created, payload.updated, payload.deleted].flatMap(toList);
    }

    return toList(payload);
};

/**
 * Pulls the job ids out of a hub payload: a planning row or array of them (`id`), a raw job (`id`),
 * a `{ created, updated, deleted }` batch, or a bare id (JobDeleted).
 */
export const extractPayloadJobIds = (payload) =>
    payloadRows(payload).map((row) => (typeof row === 'object' ? row.id : row)).filter((id) => id !== undefined && id !== null);

/** Distinct offices named in a hub payload (raw job or planning view model). */
export const extractPayloadOffices = (payload) => [
    ...new Set(
        payloadRows(payload)
            .filter((row) => typeof row === 'object')
            .map((row) => row.dispatchOfficeLocationId ?? row.dispatchLocationId)
            .filter((id) => id !== undefined && id !== null),
    ),
];

/**
 * Decides what a Dispatch+ hub message did. Dispatch+ never merges a payload: it either invalidates the
 * jobs query (then `refetchStatus` is the query status once the refetch settled) or ignores the message.
 */
export const classifyDispatchPlusEcho = ({
    invalidated, refetchStatus, dispatchGroupCompanies, payload,
}) => {
    const jobIds = extractPayloadJobIds(payload);

    if (!invalidated) {
        return {
            outcome: EchoOutcomes.officeFiltered,
            jobIds,
            detail: `group=${dispatchGroupCompanies};jobOffices=${extractPayloadOffices(payload)}`,
        };
    }

    return { outcome: refetchStatus === 'error' ? EchoOutcomes.refetchError : EchoOutcomes.refetched, jobIds };
};

/**
 * Observes a Dispatch+ hub message without changing it. `run` performs the handler and returns
 * `{ invalidated, promise }`; its result is handed back untouched and a throw is rethrown after being
 * reported. Only messages this user's own save caused are reported.
 */
export const withDispatchPlusEcho = ({
    job, opts, dispatchGroupCompanies, run, getRefetchStatus,
}) => {
    const meta = parseBroadcastMeta(opts);
    let payload = null;

    try {
        payload = JSON.parse(job);
    } catch {
        // An unreadable payload is reported with no job ids.
    }

    let result;

    try {
        result = run();
    } catch (e) {
        reportEcho(meta, { outcome: EchoOutcomes.handlerError, detail: e?.message, jobIds: extractPayloadJobIds(payload) }, 'dispatchPlus');
        throw e;
    }

    Promise.resolve(result.promise)
        .catch(() => undefined)
        .then(() => reportEcho(
            meta,
            classifyDispatchPlusEcho({
                invalidated: result.invalidated, refetchStatus: getRefetchStatus(), dispatchGroupCompanies, payload,
            }),
            'dispatchPlus',
        ));

    return result;
};
